import { doc, getDoc, setDoc, deleteDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

const SYNC_KEYS = [
  'accounts', 'cards', 'categories', 'transactions',
  'transfers', 'goals', 'budgets', 'investments', 'notifications',
  'debts', 'vehicles', 'vehicleMaintenance', 'license', 'savingsGoalMonthly',
  'salaryDivision', 'investHistory', 'investAllocation', 'passiveIncomeGoal',
];

function getPath(uid, familyId) {
  return familyId ? `families/${familyId}` : `users/${uid}`;
}

function countRecords(data) {
  return (data.accounts?.length || 0)
    + (data.transactions?.length || 0)
    + (data.goals?.length || 0)
    + (data.debts?.length || 0)
    + (data.investments?.length || 0);
}

// Sentinel para distinguir "documento não existe" de "erro de rede"
export const CLOUD_NOT_FOUND = Symbol('CLOUD_NOT_FOUND');
export const CLOUD_ERROR     = Symbol('CLOUD_ERROR');

// Rastreia ids de transações criadas por webhook (WhatsApp/Telegram) que o app
// já carregou ao menos uma vez. O merge anti-perda (saveCloudData) usa isso pra
// distinguir "tx nova que o app nunca viu" (preservar) de "tx de webhook que o
// app já viu e o usuário deletou de propósito" (deixar deletar).
const SEEN_WH_KEY = 'maw_seen_wh_tx';
const isWebhookTx = (t) => t && t.id && (t.createdVia === 'whatsapp' || t.createdVia === 'telegram');
function getSeenWhTxIds() {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_WH_KEY) || '[]')); } catch { return new Set(); }
}
function addSeenWhTxIds(ids) {
  if (!ids.length) return;
  try {
    const set = getSeenWhTxIds();
    ids.forEach(id => set.add(id));
    localStorage.setItem(SEEN_WH_KEY, JSON.stringify([...set].slice(-3000))); // cap pra não crescer sem fim
  } catch {}
}
export function recordSeenWebhookTx(transactions) {
  if (Array.isArray(transactions)) addSeenWhTxIds(transactions.filter(isWebhookTx).map(t => t.id));
}

export async function loadCloudData(uid, familyId = null) {
  try {
    const snap = await getDoc(doc(db, getPath(uid, familyId)));
    if (!snap.exists()) return CLOUD_NOT_FOUND;
    const data = snap.data();
    // Marca como "vistos" os lançamentos de webhook que vieram da nuvem agora
    recordSeenWebhookTx(data.transactions);
    return data;
  } catch (e) {
    console.warn('Firestore: falha ao carregar dados:', e.message);
    return CLOUD_ERROR;
  }
}

// Escuta o documento do usuário em tempo real e avisa quando aparecem
// transações NOVAS que o app ainda não tem (ex: lançamento feito pelo WhatsApp/
// Telegram enquanto o app está aberto, ou por outro aparelho). Resolve a corrida
// na origem: o app fica sabendo na hora, em vez de sobrescrever sem saber.
//
// - Ignora `hasPendingWrites` (o eco das próprias gravações do app) → sem loop.
// - `getLocalTxIds()` devolve o Set de ids que o app já tem no estado.
// - Só puxa ids inéditos, e nunca ressuscita um lançamento de webhook que o
//   usuário deletou de propósito (seen-set).
// Retorna a função de unsubscribe.
export function subscribeCloudTransactions(uid, familyId, getLocalTxIds, onNewTransactions) {
  const ref = doc(db, getPath(uid, familyId));
  return onSnapshot(ref, (snap) => {
    if (!snap.exists() || snap.metadata.hasPendingWrites) return;
    const cloud = snap.data();
    if (!Array.isArray(cloud.transactions)) return;
    const localIds = getLocalTxIds();
    const seen = getSeenWhTxIds();
    const news = cloud.transactions.filter(t =>
      t && t.id && !localIds.has(t.id) && !(isWebhookTx(t) && seen.has(t.id))
    );
    if (news.length) {
      recordSeenWebhookTx(news);
      onNewTransactions(news);
    }
  }, (err) => console.warn('MAW: listener de tempo real falhou:', err.message));
}

// Assinatura Kiwify — escrita pelo webhook no MAW Admin em subscriptions/{email}
// Retorna { plan, status, expiresAt } ou null
export async function loadSubscription(email) {
  if (!email) return null;
  try {
    const snap = await getDoc(doc(db, `subscriptions/${email.toLowerCase().trim()}`));
    return snap.exists() ? snap.data() : null;
  } catch (e) {
    console.warn('Firestore: falha ao verificar assinatura:', e.message);
    return null;
  }
}

// Backup diário dos dados da nuvem (slot A)
export function backupToLocalStorage(state) {
  try {
    const today = new Date().toISOString().slice(0, 10);
    const lastBackup = localStorage.getItem('maw_backup_date');
    if (lastBackup === today) return;
    const payload = {};
    SYNC_KEYS.forEach(k => { if (state[k] !== undefined) payload[k] = state[k]; });
    if (countRecords(payload) === 0) return;
    localStorage.setItem('maw_backup', JSON.stringify(payload));
    localStorage.setItem('maw_backup_date', today);
    console.info('MAW: backup diário salvo (' + today + ')');
  } catch {}
}

// Backup pré-sincronização — salvo ANTES de qualquer operação com a nuvem (slot B)
// Protege contra falhas durante o carregamento/salvamento inicial
export function savePreSyncBackup(state) {
  try {
    const payload = {};
    SYNC_KEYS.forEach(k => { if (state[k] !== undefined) payload[k] = state[k]; });
    if (countRecords(payload) === 0) return;
    const ts = new Date().toISOString();
    localStorage.setItem('maw_pre_backup', JSON.stringify(payload));
    localStorage.setItem('maw_pre_backup_ts', ts);
    console.info('MAW: backup pré-sync salvo (' + ts + ')');
  } catch {}
}

export function getBackupInfo() {
  return {
    daily: {
      data: localStorage.getItem('maw_backup'),
      date: localStorage.getItem('maw_backup_date'),
    },
    preSync: {
      data: localStorage.getItem('maw_pre_backup'),
      ts:   localStorage.getItem('maw_pre_backup_ts'),
    },
  };
}

export async function deleteCloudData(uid, familyId = null) {
  try {
    await deleteDoc(doc(db, getPath(uid, familyId)));
    // Limpa backups locais também — limpeza foi intencional
    localStorage.removeItem('maw_backup');
    localStorage.removeItem('maw_backup_date');
    localStorage.removeItem('maw_pre_backup');
    localStorage.removeItem('maw_pre_backup_ts');
    console.info('MAW: dados da nuvem excluídos.');
  } catch (e) {
    console.warn('MAW: falha ao excluir dados da nuvem:', e.message);
    throw e;
  }
}

export async function saveCloudData(uid, state, familyId = null) {
  const data = {};
  SYNC_KEYS.forEach(k => { if (state[k] !== undefined) data[k] = state[k]; });

  // Nunca salvar licença DEMO no Firestore
  if (data.license?.key === 'DEMO') delete data.license;

  // Salvar perfil básico para rastreamento de trial/marketing no Admin
  if (state.user?.email && state.user.email !== 'demo@maw.com') {
    data.profile = {
      email: state.user.email,
      name: state.user.name ?? null,
      phone: state.user.phone ?? null,
      createdAt: state.user.createdAt ?? null,
    };
  }

  // Nunca sobrescrever a nuvem com estado vazio
  const newCount = countRecords(data);
  if (newCount === 0 && data.accounts !== undefined) {
    console.warn('Firestore: bloqueado — estado vazio, abortando save.');
    return;
  }

  // Lê a nuvem UMA vez — serve para a proteção de regressão E para o merge de
  // lançamentos do WhatsApp/Telegram (abaixo).
  let cloudData = null;
  try {
    const existingSnap = await getDoc(doc(db, getPath(uid, familyId)));
    if (existingSnap.exists()) cloudData = existingSnap.data();
  } catch {}

  if (cloudData) {
    // Nunca sobrescrever se a nuvem já tem muito mais dados (proteção contra regressão)
    const cloudCount = countRecords(cloudData);
    if (cloudCount > 0 && newCount < cloudCount * 0.5) {
      console.warn(`Firestore: bloqueado — nuvem tem ${cloudCount} registros, novo estado tem apenas ${newCount}. Abortando para evitar perda.`);
      return;
    }

    // MERGE anti-perda: o webhook do WhatsApp/Telegram adiciona transações
    // direto na nuvem (arrayUnion). Se o app está aberto e não conhece esses
    // lançamentos, este setDoc os apagaria. Recupera do documento da nuvem as
    // transações de webhook que o estado local ainda não tem E que o app nunca
    // carregou (não foram deletadas de propósito) — e reaplica o efeito no saldo.
    if (Array.isArray(data.transactions) && Array.isArray(cloudData.transactions)) {
      const localIds = new Set(data.transactions.map(t => t && t.id));
      const seen = getSeenWhTxIds();
      const recovered = cloudData.transactions.filter(t =>
        isWebhookTx(t) && !localIds.has(t.id) && !seen.has(t.id)
      );
      if (recovered.length) {
        data.transactions = [...recovered, ...data.transactions];
        // Reaplica o delta no saldo (só transação de conta paga; cartão não mexe no saldo)
        if (Array.isArray(data.accounts)) {
          data.accounts = data.accounts.map(a => {
            const delta = recovered
              .filter(t => t.accountId === a.id && t.status === 'paid' && !t.cardId)
              .reduce((s, t) => s + (t.type === 'income' ? t.value : -t.value), 0);
            return delta ? { ...a, balance: Math.round((a.balance + delta) * 100) / 100 } : a;
          });
        }
        // Mesma coisa na fatura do cartão: sem isso o gasto voltava pro extrato
        // mas o "Fatura atual" do cartão ficava menor que a fatura de verdade
        if (Array.isArray(data.cards)) {
          data.cards = data.cards.map(c => {
            const delta = recovered
              .filter(t => t.cardId === c.id && t.type === 'expense')
              .reduce((s, t) => s + (t.value || 0), 0);
            return delta ? { ...c, used: Math.max(0, Math.round(((c.used || 0) + delta) * 100) / 100) } : c;
          });
        }
        console.info(`MAW: ${recovered.length} lançamento(s) do WhatsApp/Telegram preservados no merge.`);
      }
    }
  }
  // NÃO marcar "visto" aqui: um lançamento só é "visto" quando o app o carrega de
  // fato no estado (loadCloudData). Marcar no save faria o save seguinte apagá-lo,
  // já que o estado em memória ainda não o contém até um reload.

  await setDoc(doc(db, getPath(uid, familyId)), data);

  // Ponteiro leve em users/{uid} → familyId. O documento financeiro em si vive em
  // families/{familyId} quando a pessoa está numa família, mas o backend (webhooks
  // do WhatsApp/Telegram) só conhece o uid de quem mandou a mensagem — sem este
  // ponteiro ele não tem como saber que deve lançar no documento compartilhado.
  try {
    await setDoc(doc(db, `users/${uid}`), { familyId: familyId || null }, { merge: true });
  } catch (e) {
    console.warn('Firestore: falha ao atualizar ponteiro de família:', e.message);
  }
}
